import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getPublishedPost, listPublishedPosts, type PublicPost } from "@/lib/blog.functions";
import { ContextualLinks } from "@/components/ContextualLinks";
import {
  cityLinksForTopic,
  guidesForTopics,
  inferTopics,
  servicesForTopics,
} from "@/lib/internalLinks";

const SLUG_REDIRECTS: Record<string, string> = {
  "denver-home-theater-installation-cost": "planning-a-home-theater",
  "control4-vs-crestron-vs-savant": "choosing-a-smart-home-platform",
  "lutron-vs-ketra-smart-lighting-denver": "smart-lighting-and-shading-basics",
  "outdoor-tv-installation-colorado-weather": "outdoor-tv-and-audio-considerations",
  "wifi-mesh-vs-access-points-denver": "home-wifi-that-actually-works",
  "smart-home-prewire-checklist-new-construction": "low-voltage-prewire-checklist",
};

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const target = SLUG_REDIRECTS[params.slug];
    if (target) {
      throw redirect({
        to: "/blog/$slug",
        params: { slug: target },
        statusCode: 301,
      });
    }
    const [post, all] = await Promise.all([
      getPublishedPost({ data: { slug: params.slug } }),
      listPublishedPosts(),
    ]);
    if (!post) throw notFound();
    const related = all.filter((p) => p.slug !== post.slug).slice(0, 3);
    return { post, related };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return { meta: [{ title: "Article not found | Apex AV" }] };
    }
    const url = `https://apexav.com/blog/${post.slug}`;
    const date = post.published_at ?? post.updated_at;
    return {
      meta: [
        { title: `${post.title} | Apex AV` },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: date },
        { property: "article:modified_time", content: post.updated_at },
        { property: "article:section", content: post.category },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: date,
            dateModified: post.updated_at,
            author: { "@type": "Organization", name: "Apex AV" },
            publisher: { "@id": "https://apexav.com/#organization" },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: post.category,
          }),
        },
      ],

    };
  },
  notFoundComponent: () => (
    <section className="container mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl font-bold">Article not found</h1>
      <p className="mt-4 text-muted-foreground">That post doesn't exist or has been moved.</p>
      <Link to="/blog" className="mt-8 inline-flex items-center gap-2 text-brand-text font-semibold">
        <ArrowLeft className="h-4 w-4" /> Back to blog
      </Link>
    </section>
  ),
  errorComponent: ({ error }) => (
    <section className="container mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl font-bold">Something went wrong</h1>
      <p className="mt-4 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </section>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post, related } = Route.useLoaderData() as { post: PublicPost; related: PublicPost[] };
  const date = post.published_at ?? post.updated_at;
  const topics = inferTopics(post);
  const primaryTopic = topics[0];

  return (
    <>
      <article className="container mx-auto px-6 pt-16 pb-20 max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <p className="mt-8 text-sm font-semibold text-brand-text uppercase tracking-wider">
          {post.category}
        </p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">
          {post.title}
        </h1>
        <div className="mt-5 flex items-center gap-5 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {new Date(date).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
              timeZone: "UTC",
            })}

          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {post.read_minutes} min read
          </span>
        </div>

        <div className="prose prose-invert mt-10 max-w-none prose-headings:font-display prose-h2:mt-12 prose-h2:text-3xl prose-h2:font-bold prose-h3:text-xl prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-a:text-brand-text">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
        </div>

        <div className="mt-16 rounded-xl border border-border bg-card/40 p-8 text-center">
          <h2 className="font-display text-2xl font-bold">Planning a project in Denver?</h2>
          <p className="mt-3 text-muted-foreground">
            Free in-home consultations across the Front Range.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow hover:opacity-90 transition"
          >
            Book a Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>

      <ContextualLinks
        heading={`Where to go next with ${post.category.toLowerCase()}`}
        intro="Pages we referenced above, plus the Denver-area communities where we install this work most often."
        groups={[
          {
            heading: "Services in this article",
            icon: "service",
            links: servicesForTopics(topics, 4),
          },
          {
            heading: "Related guides",
            icon: "guide",
            links: guidesForTopics(topics, 4, post.slug),
          },
          {
            heading: "Denver-area service areas",
            icon: "area",
            links: cityLinksForTopic(primaryTopic, 5),
          },
        ]}
      />



      {related.length > 0 && (
        <section className="container mx-auto px-6 pb-24">
          <h2 className="font-display text-2xl font-bold">Keep reading</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to="/blog/$slug"
                params={{ slug: r.slug }}
                className="group rounded-xl border border-border bg-card/40 p-6 hover:border-brand-text/50 transition-colors"
              >
                <p className="text-xs font-semibold text-brand-text uppercase tracking-wider">
                  {r.category}
                </p>
                <h3 className="mt-3 font-display text-lg font-bold leading-tight group-hover:text-brand-text transition-colors">
                  {r.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
